export const humanize = (str) => {
  if (!str) {
    return '';
  }

  // Coerce: callers pass values straight off API payloads, and a non-string
  // (e.g. a numeric role id) would otherwise throw and take the whole render
  // down rather than degrading to a readable label.
  const frags = String(str).split('_');
  for (let i = 0; i < frags.length; i++) {
    frags[i] = frags[i].charAt(0).toUpperCase() + frags[i].slice(1);
  }
  return frags.join(' ');
};

const ROLE_I18N_KEYS = {
  petasense_admin: 'roles.petasenseAdmin',
  petasense_viewer: 'roles.petasenseViewer',
  account_admin: 'roles.accountAdmin',
  admin: 'roles.accountAdmin',
  // Third-highest privilege in UserRoleEnum and returned by
  // select_highest_privilege_role, but it was missing here -- so it fell back to
  // humanize() and rendered "Org Admin" in every locale despite roles.orgAdmin
  // already being translated in all five bundles.
  org_admin: 'roles.orgAdmin',
  analyst: 'roles.analyst',
  assembler: 'roles.assembler',
  technician: 'roles.technician',
  manager: 'roles.manager',
  partner_admin: 'roles.partnerAdmin',
  developer: 'roles.developer'
};

/**
 * Returns a localized label for a role (e.g. `account_admin` -> "Account Admin"),
 * falling back to `humanize(role)` if no translation exists.
 */
export const humanizeRole = (role, t) => {
  if (!role) return '';
  const key = ROLE_I18N_KEYS[String(role)];
  if (!key || typeof t !== 'function') return humanize(role);
  return t(key, { defaultValue: humanize(role) });
};

// Maps default (system) event-definition `name` identifiers to i18n keys. Custom,
// user-created definition names are not listed and fall back to `humanize(name)`.
const EVENT_DEFINITION_I18N_KEYS = {
  battery_level_critical: 'eventDefinitions.defaultNames.batteryLevelCritical',
  battery_level_warning: 'eventDefinitions.defaultNames.batteryLevelWarning',
  health_score_critical: 'eventDefinitions.defaultNames.healthScoreCritical',
  health_score_warning: 'eventDefinitions.defaultNames.healthScoreWarning',
  machine_condition_critical: 'eventDefinitions.defaultNames.machineConditionCritical',
  machine_condition_warning: 'eventDefinitions.defaultNames.machineConditionWarning',
  temperature_critical: 'eventDefinitions.defaultNames.temperatureCritical',
  temperature_warning: 'eventDefinitions.defaultNames.temperatureWarning',
  tx_battery_level_critical: 'eventDefinitions.defaultNames.txBatteryLevelCritical',
  tx_battery_level_warning: 'eventDefinitions.defaultNames.txBatteryLevelWarning',
  mote_unreachable: 'eventDefinitions.defaultNames.moteUnreachable',
  current_envelope_alarm: 'eventDefinitions.defaultNames.currentEnvelopeAlarm',
  tx_unreachable: 'eventDefinitions.defaultNames.txUnreachable',
  vibration_warning: 'eventDefinitions.defaultNames.vibrationWarning',
  vibration_critical: 'eventDefinitions.defaultNames.vibrationCritical',
  vibration_acceleration_warning: 'eventDefinitions.defaultNames.vibrationAccelerationWarning',
  vibration_acceleration_critical: 'eventDefinitions.defaultNames.vibrationAccelerationCritical',
  vibration_envelope_alarm: 'eventDefinitions.defaultNames.vibrationEnvelopeAlarm',
  vibration_acceleration_envelope_alarm: 'eventDefinitions.defaultNames.vibrationAccelerationEnvelopeAlarm',
  vibration_demod_warning: 'eventDefinitions.defaultNames.vibrationDemodWarning',
  vibration_demod_critical: 'eventDefinitions.defaultNames.vibrationDemodCritical',
  pressure_critical: 'eventDefinitions.defaultNames.pressureCritical',
  pressure_warning: 'eventDefinitions.defaultNames.pressureWarning',
  ultrasound_critical: 'eventDefinitions.defaultNames.ultrasoundCritical',
  ultrasound_warning: 'eventDefinitions.defaultNames.ultrasoundWarning',
  // Overall-alarm templates are named `<tag_type>_<alarm_level>`, so the current
  // tag type yields `current_*` — not `rms_current_*`, which is a trigger-parameter
  // name and never an event-template name.
  current_critical: 'eventDefinitions.defaultNames.currentCritical',
  current_warning: 'eventDefinitions.defaultNames.currentWarning',
  other: 'eventDefinitions.defaultNames.other'
};

/**
 * Returns a localized label for a default event-definition name
 * (e.g. `battery_level_critical` -> "Battery Level Critical"), falling back to
 * `humanize(name)` for custom definitions or when no translation exists.
 */
export const humanizeEventDefinition = (name, t) => {
  if (!name) return '';
  const key = EVENT_DEFINITION_I18N_KEYS[String(name)];
  if (!key || typeof t !== 'function') return humanize(name);
  return t(key, { defaultValue: humanize(name) });
};

/**
 * Title-cases a snake_case identifier: `axial_pump` -> "Axial Pump".
 *
 * Deliberately separate from humanize() rather than folded into it. The two
 * differ in ways that matter to their callers: this drops empty fragments, so
 * `a__b` renders "A B" rather than "A  B", and it formats 0 as "0" instead of
 * "". humanize() has far too many call sites to change either behaviour safely.
 */
export const titleCase = (value) => {
  if (value === null || value === undefined) return '';
  return String(value)
    .split('_')
    .filter(Boolean)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};
